/**
 * ReadinessSummaryPanel — Phase 2E PR-2 (Feb 2026)
 * ---------------------------------------------------------------------------
 * Reusable participant readiness surface. Two mount modes:
 *
 *   mode="full"    — Participant Detail page (banner at the top of the page).
 *                    Calls GET /api/participants/{pid}/readiness.
 *                    Shows verdict + 12 check rows with evidence + remediation hints.
 *
 *   mode="banner"  — Intake Wizard (header strip).
 *                    Calls GET /api/participants/{pid}/readiness ONLY when the
 *                    intake has already been activated (intake.promoted_participant_id
 *                    is set). Otherwise renders a tasteful placeholder explaining
 *                    that readiness becomes available after activation.
 *
 * Feature flag: when `participant_readiness_engine_enabled` is OFF the
 * component renders nothing (the backend returns 404 anyway — we hard-gate
 * client-side to avoid a noisy request).
 *
 * v1 boundary: PURELY INFORMATIONAL. The UI MUST NOT imply that a NOT_READY
 * participant is blocked from any downstream action. Wording leans on
 * "Readiness check" rather than "blocked".
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CheckCircle2, AlertTriangle, AlertCircle, HelpCircle,
  RefreshCw, ShieldCheck, ChevronDown, ChevronUp,
} from 'lucide-react';

const FLAG_KEY = 'participant_readiness_engine_enabled';

// Stable display order for checks (mirrors backend `engine.py` order).
const CHECK_ORDER = [
  'demographics_complete',
  'funding_plan_active',
  'service_agreement_signed',
  'required_documents_uploaded',
  'risk_assessment_complete',
  'emergency_contact_present',
  'coordinator_assigned',
  'service_allocations',
  'support_worker_assigned',
  'restrictive_practices_review_current',
  'anaphylaxis_alert_acknowledged',
  'consents_signed',
];

const VERDICT_TONE = {
  READY:                'bg-emerald-50 border-emerald-300 text-emerald-900',
  READY_WITH_WARNINGS:  'bg-amber-50 border-amber-300 text-amber-900',
  NOT_READY:            'bg-orange-50 border-orange-300 text-orange-900',
  INCONCLUSIVE:         'bg-slate-50 border-slate-300 text-slate-700',
};

const VERDICT_LABEL = {
  READY:               'Ready',
  READY_WITH_WARNINGS: 'Ready (with warnings)',
  NOT_READY:           'Not yet ready',
  INCONCLUSIVE:        'Inconclusive',
};

const STATUS_ICON = {
  PASS:           <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
  FAIL:           <AlertCircle className="h-4 w-4 text-orange-600" />,
  INCONCLUSIVE:   <HelpCircle className="h-4 w-4 text-slate-500" />,
  NOT_APPLICABLE: <HelpCircle className="h-4 w-4 text-slate-400" />,
};

const STATUS_TONE = {
  PASS:           'bg-emerald-100 text-emerald-800 border-emerald-300',
  FAIL:           'bg-orange-100 text-orange-800 border-orange-300',
  INCONCLUSIVE:   'bg-slate-100 text-slate-700 border-slate-300',
  NOT_APPLICABLE: 'bg-slate-100 text-slate-500 border-slate-200',
};


function sortChecks(checks) {
  const idx = Object.fromEntries(CHECK_ORDER.map((k, i) => [k, i]));
  return [...(checks || [])].sort(
    (a, b) => (idx[a.key] ?? 99) - (idx[b.key] ?? 99)
  );
}


function VerdictHeader({ verdict, summary, generatedAt, onRefresh }) {
  const tone = VERDICT_TONE[verdict] || VERDICT_TONE.INCONCLUSIVE;
  const label = VERDICT_LABEL[verdict] || verdict;
  const reqPct = summary?.required_total
    ? `${summary.required_passed}/${summary.required_total} required passed`
    : '';
  return (
    <div
      className={`rounded-md border px-3 py-2 flex items-center justify-between gap-3 ${tone}`}
      data-testid="readiness-verdict-header"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldCheck className="h-4 w-4" />
        <span className="font-semibold text-sm" data-testid="readiness-verdict-label">
          Readiness check — {label}
        </span>
        {reqPct && (
          <span className="text-xs opacity-80" data-testid="readiness-required-summary">
            · {reqPct}
          </span>
        )}
        {summary?.warning_failed > 0 && (
          <span className="text-xs opacity-80" data-testid="readiness-warnings-summary">
            · {summary.warning_failed} warning{summary.warning_failed > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {generatedAt && (
          <span className="text-[10px] opacity-60 hidden sm:inline">
            {new Date(generatedAt).toLocaleString()}
          </span>
        )}
        {onRefresh && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onRefresh}
            data-testid="readiness-refresh-btn"
            className="h-7"
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}


function CheckRow({ check }) {
  const icon = STATUS_ICON[check.status] || STATUS_ICON.INCONCLUSIVE;
  const tone = STATUS_TONE[check.status] || STATUS_TONE.INCONCLUSIVE;
  return (
    <li
      className="flex items-start gap-2 py-1.5"
      data-testid={`readiness-check-${check.key}`}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{check.label || check.key}</span>
          <Badge variant="outline" className={`${tone} text-[10px] px-1.5`}>
            {check.status}
          </Badge>
          {check.required ? (
            <span className="text-[10px] text-slate-500">REQUIRED</span>
          ) : (
            <span className="text-[10px] text-slate-400">Warning</span>
          )}
        </div>
        {check.hint && (
          <p
            className="text-xs text-slate-600 mt-0.5"
            data-testid={`readiness-check-hint-${check.key}`}
          >
            {check.hint}
          </p>
        )}
      </div>
    </li>
  );
}


// ============================================================================
// Main component
// ============================================================================
export function ReadinessSummaryPanel({
  participantId,
  mode = 'full',
  intake = null,         // for mode='banner' — pass the intake doc so we can read promoted_participant_id
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [flagEnabled, setFlagEnabled] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Resolve which participant id to query.
  // - mode='full': caller passes participantId directly.
  // - mode='banner' (intake context): use intake.promoted_participant_id only.
  //   Before activation it's null and we skip the API call.
  const effectivePid = mode === 'banner'
    ? (intake?.promoted_participant_id || null)
    : (participantId || null);

  // Client-side feature-flag gate (hard gate — no API call when off).
  const loadFlag = useCallback(() => {
    try {
      const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
      setFlagEnabled(!!flags[FLAG_KEY]);
    } catch {
      setFlagEnabled(false);
    }
  }, []);

  const loadReadiness = useCallback(async () => {
    if (flagEnabled !== true) { setData(null); return; }
    if (!effectivePid) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/participants/${effectivePid}/readiness`);
      setData(r.data?.data || null);
    } catch (e) {
      const code = e.response?.data?.error?.code || e.response?.data?.detail?.code;
      if (code === 'FEATURE_DISABLED') {
        setData(null);
        setFlagEnabled(false);
      } else if (code === 'PARTICIPANT_NOT_FOUND') {
        setData(null);
      } else {
        setError(e.response?.data?.error?.message || e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [effectivePid, flagEnabled]);

  useEffect(() => { loadFlag(); }, [loadFlag]);
  useEffect(() => { loadReadiness(); }, [loadReadiness]);

  // ── Flag OFF → render nothing (no API call, no surface).
  if (flagEnabled !== true) return null;

  // ── Intake-banner pre-activation: show a calm placeholder.
  if (mode === 'banner' && !effectivePid) {
    return (
      <Card
        className="border-dashed bg-slate-50/50"
        data-testid="readiness-panel-intake-preactivation"
      >
        <CardContent className="p-3 flex items-center gap-2 text-xs text-slate-600">
          <ShieldCheck className="h-3.5 w-3.5 text-slate-500" />
          <span>
            Readiness check becomes available once the participant is activated.
            It is informational only and does not block activation.
          </span>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card data-testid={`readiness-panel-${mode}-loading`}>
        <CardContent className="p-3 space-y-2">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-4 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200" data-testid={`readiness-panel-${mode}-error`}>
        <CardContent className="p-3 text-xs text-red-700">
          Could not load readiness check: {error}
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const checks = sortChecks(data.checks);

  // ── Banner mode (intake wizard): compact, collapsed by default.
  if (mode === 'banner') {
    return (
      <Card
        className="bg-white/50"
        data-testid="readiness-panel-banner"
      >
        <CardContent className="p-3 space-y-2">
          <VerdictHeader
            verdict={data.verdict}
            summary={data.summary}
            generatedAt={data.generated_at}
            onRefresh={loadReadiness}
          />
          <div className="text-[11px] text-slate-500" data-testid="readiness-informational-disclaimer">
            Informational only — readiness does not block activation or service delivery in v1.
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded(!expanded)}
            className="h-7 text-xs"
            data-testid="readiness-toggle-details"
          >
            {expanded ? (
              <><ChevronUp className="h-3 w-3 mr-1" /> Hide details</>
            ) : (
              <><ChevronDown className="h-3 w-3 mr-1" /> Show 12-check details</>
            )}
          </Button>
          {expanded && (
            <ul className="divide-y divide-slate-100 border-t pt-2" data-testid="readiness-check-list">
              {checks.map((c) => <CheckRow key={c.key} check={c} />)}
            </ul>
          )}
        </CardContent>
      </Card>
    );
  }

  // ── Full mode (Participant Detail page).
  return (
    <Card className="bg-white" data-testid="readiness-panel-full">
      <CardContent className="p-3 space-y-3">
        <VerdictHeader
          verdict={data.verdict}
          summary={data.summary}
          generatedAt={data.generated_at}
          onRefresh={loadReadiness}
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setExpanded(!expanded)}
          className="h-7 text-xs -mt-1"
          data-testid="readiness-toggle-details"
        >
          {expanded ? (
            <><ChevronUp className="h-3 w-3 mr-1" /> Hide details</>
          ) : (
            <><ChevronDown className="h-3 w-3 mr-1" /> Show details</>
          )}
        </Button>
        {expanded && (
          <>
          <p
            className="text-[11px] text-slate-500"
            data-testid="readiness-informational-disclaimer"
          >
            Informational only — this check does not block shifts or service delivery in v1.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Required checks
              </div>
              <ul className="divide-y divide-slate-100" data-testid="readiness-required-list">
                {checks.filter((c) => c.required).map((c) => (
                  <CheckRow key={c.key} check={c} />
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Warnings
              </div>
              <ul className="divide-y divide-slate-100" data-testid="readiness-warnings-list">
                {checks.filter((c) => !c.required).map((c) => (
                  <CheckRow key={c.key} check={c} />
                ))}
              </ul>
            </div>
          </div>
          {data.warnings?.length > 0 && (
            <div
              className="text-[11px] text-amber-700 flex items-start gap-1"
              data-testid="readiness-engine-warnings"
            >
              <AlertTriangle className="h-3 w-3 mt-0.5" />
              <span>
                Some signals are unavailable
                {data.warnings.map((w) => ` — ${w.message || w.code}`).join('')}
              </span>
            </div>
          )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default ReadinessSummaryPanel;
